'use client';

import { createElement, useCallback, useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import {
  PlusIcon,
  ChevronDownIcon,
  FolderIcon,
  AudioLinesIcon,
  ClipboardListIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { selectableMatterTypes } from '@/lib/config/selectable-matter-types';
import { getMatterTypesAction } from '@/lib/services/matter-types';
import { gatedAction } from '@/lib/api/auth-gate';
import { useCreateMatter } from '@/lib/contexts/create-matter-context';
import { CreateMatterFormModal } from './create-matter-form-modal';
import { MatterType } from '@/lib/services/matter-types/types';
import { getMatterTypeIcon } from '../quick-actions/matter-types-view';
import { cn } from '@/lib/utils';
import { useOptionalFeatureFlags } from '@/lib/providers/feature-flags-provider';

interface CreateMatterSidebarTriggerProps {
  /** Render as icon-only button for the icon rail */
  compact?: boolean;
  className?: string;
  /** Notified on every open/close of the Create menu, so a parent nav can react (e.g. dim itself while the menu is open) */
  onMenuOpenChange?: (open: boolean) => void;
}

/**
 * "Create" trigger for the sidebar and icon rail.
 * Opens a menu with Matter, Scribe session, and Intake form entry points;
 * selecting Matter preserves the existing create-matter flow exactly (a
 * direct create, or a matter-type submenu, depending on the `matter-types`
 * flag). Scribe session and Intake form are always offered.
 * Keyboard shortcut: Cmd/Ctrl+M
 */
export function CreateMatterSidebarTrigger({
  compact = false,
  className,
  onMenuOpenChange
}: CreateMatterSidebarTriggerProps) {
  const router = useRouter();
  const { openIntermediary } = useCreateMatter();
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedMatterType, setSelectedMatterType] =
    useState<MatterType | null>(null);
  const { hasFeature } = useOptionalFeatureFlags();

  // VALE-1705 — the list is fetched unconditionally now. It used to be
  // `enabled: hasFeature('matter-types')`, which was the same statement as the
  // direct-create fallback below: with the flag off there was exactly one type
  // a firm could open, so there was nothing to fetch and nothing to choose
  // between. Estate planning is available to every firm, so the un-flagged firm
  // has a choice too and the submenu has to be able to render it.
  const { data: allMatterTypes = [] } = useQuery({
    queryKey: ['matter-types'],
    queryFn: () => gatedAction(() => getMatterTypesAction()),
    staleTime: Infinity,
    gcTime: Infinity
  });

  const matterTypes = selectableMatterTypes(
    allMatterTypes,
    hasFeature('matter-types')
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setMenuOpen(open);
      onMenuOpenChange?.(open);
    },
    [onMenuOpenChange]
  );

  // Keyboard shortcut: Cmd/Ctrl+M — toggles the Create menu.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'm') {
        e.preventDefault();
        setMenuOpen((prev) => {
          const next = !prev;
          onMenuOpenChange?.(next);
          return next;
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onMenuOpenChange]);

  const handleMatterTypeSelect = (matterType: MatterType) => {
    if (matterType.slug === 'estate-administration') {
      openIntermediary();
    } else {
      setSelectedMatterType(matterType);
    }
  };

  // A submenu only earns its extra click when there is genuinely something to
  // choose between. Derived from the served list rather than from the flag, so
  // a firm offered a single type still gets the one-click direct create.
  const matterMenuItem =
    matterTypes.length > 1 ? (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <FolderIcon className="mr-2 h-4 w-4" />
          Matter
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="w-64">
          {matterTypes.map((type) => (
            <DropdownMenuItem
              key={type.slug}
              onClick={() => handleMatterTypeSelect(type)}
              className="cursor-pointer"
            >
              {createElement(getMatterTypeIcon(type.slug), {
                className: 'mr-2 h-4 w-4'
              })}
              <span className="flex-1">{type.label}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    ) : (
      <DropdownMenuItem onClick={openIntermediary} className="cursor-pointer">
        <FolderIcon className="mr-2 h-4 w-4" />
        Matter
      </DropdownMenuItem>
    );

  const menuContent = (
    <DropdownMenuContent
      align="start"
      side={compact ? 'right' : 'bottom'}
      className="w-56"
    >
      {matterMenuItem}
      <DropdownMenuItem
        onClick={() => router.push('/scribe/new')}
        className="cursor-pointer"
      >
        <AudioLinesIcon className="mr-2 h-4 w-4" />
        Scribe session
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => router.push('/intake/new')}
        className="cursor-pointer"
      >
        <ClipboardListIcon className="mr-2 h-4 w-4" />
        Intake form
      </DropdownMenuItem>
    </DropdownMenuContent>
  );

  const trigger = compact ? (
    <Tooltip delayDuration={0}>
      <DropdownMenu open={menuOpen} onOpenChange={handleOpenChange}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className={cn('h-8 w-8 rounded-lg', className)}
            >
              <PlusIcon className="h-4 w-4" />
              <span className="sr-only">Create</span>
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        {menuContent}
      </DropdownMenu>
      <TooltipContent side="right" sideOffset={8}>
        Create (⌘M)
      </TooltipContent>
    </Tooltip>
  ) : (
    <DropdownMenu open={menuOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('bg-card h-8 w-full rounded-lg', className)}
        >
          <PlusIcon className="mr-2 h-4 w-4" />
          <span>Create</span>
          <ChevronDownIcon className="ml-auto h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      {menuContent}
    </DropdownMenu>
  );

  return (
    <>
      {trigger}

      {selectedMatterType && (
        <CreateMatterFormModal
          matterType={selectedMatterType}
          open={!!selectedMatterType}
          onOpenChange={(open) => !open && setSelectedMatterType(null)}
        />
      )}
    </>
  );
}
